import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Shipment } from '@prlw/core/shipments/shipment.entity';
import {
  SHIPMENTS_GATEWAY,
  ShipmentsGateway,
} from '@prlw/core/shipments/shipments.gateway';
import { ShipmentsState } from '@prlw/core/shipments/shipments.state';
import { combineLatest, EMPTY, Observable, Subject } from 'rxjs';
import {
  first,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { ProductGroup } from '../products/product-group.entity';
import { ProductGroupsService } from '../products/product-groups.service';
import { PinInstrumentService } from '../monitor/pin-instrument.service';

function produceCacheKey(groups: ProductGroup[]): string {
  return groups
    .map((product) => product.code)
    .sort()
    .join();
}

@Injectable({
  providedIn: 'root',
})
export class ShipmentsProvider implements OnDestroy {
  private readonly _cache = new Map<string, Observable<Shipment[]>>();
  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(SHIPMENTS_GATEWAY) private readonly gateway: ShipmentsGateway,
    private readonly shipmentsState: ShipmentsState,
    private readonly productGroupsService: ProductGroupsService,
    private readonly pinInstrumentService: PinInstrumentService,
  ) {
    productGroupsService.currentProductGroup$
      .pipe(
        map((group) => group.code),
        switchMap((productGroup) =>
          shipmentsState.getSelectedShipments(productGroup).pipe(
            take(1),
            switchMap((selectedShipments) => {
              if (selectedShipments.length) {
                return EMPTY;
              }
              return this.getShipmentsByProductGroupCodesList([
                productGroup,
              ]).pipe(map((shipments) => shipments.map((el) => el.code)));
            }),
            map((shipmentsCodes) => ({ productGroup, shipmentsCodes })),
          ),
        ),
      )
      .subscribe(({ productGroup, shipmentsCodes }) => {
        shipmentsState.setSelectedShipments(productGroup, shipmentsCodes);
      });

    this.unselectedShipmentsForCurrentProductGroup$
      .pipe(
        withLatestFrom(pinInstrumentService.pinnedInstrument$),
        takeUntil(this._destroy$),
      )
      .subscribe(([shipments, pinnedInstrument]) => {
        if (!pinnedInstrument) return;
        if (
          shipments
            .map((shipment) => shipment.code)
            .includes(pinnedInstrument?.shipment.code)
        ) {
          pinInstrumentService.pin();
        }
      });
  }

  public getShipmentsByProductGroupsList(
    groups: ProductGroup[],
  ): Observable<Shipment[]> {
    const cacheKey = produceCacheKey(groups);
    const cachedResponse$ = this._cache.get(cacheKey);
    if (cachedResponse$) {
      return cachedResponse$;
    }
    const response$ = this.gateway
      .getShipmentsByProductGroupsList(groups)
      .pipe(shareReplay(1));
    this._cache.set(cacheKey, response$);
    return response$;
  }

  public getShipmentsByProductGroupCodesList(
    productCodes: string[],
  ): Observable<Shipment[]> {
    const groups: ProductGroup[] = productCodes.map((code) => ({
      name: '',
      code,
    }));
    return this.getShipmentsByProductGroupsList(groups);
  }

  public get shipmentsByAllProductGroup$(): Observable<Shipment[]> {
    return this.productGroupsService.productGroups$.pipe(
      switchMap((products) => this.getShipmentsByProductGroupsList(products)),
    );
  }

  public get shipmentsByCurrentProductGroup$(): Observable<Shipment[]> {
    return this.productGroupsService.currentProductGroup$.pipe(
      switchMap((product) => this.getShipmentsByProductGroupsList([product])),
    );
  }

  public get unselectedShipmentsForCurrentProductGroup$(): Observable<
    Shipment[]
  > {
    return combineLatest([
      this.shipmentsByCurrentProductGroup$,
      this._getSelectedShipmentCodesByCurrentProductGroup(),
    ]).pipe(
      map(([shipments, selectedShipments]) =>
        shipments.filter(
          (shipment) => !selectedShipments.includes(shipment.code),
        ),
      ),
    );
  }

  public addShipmentForCurrentProductGroup(shipmentCode: string): void {
    this.productGroupsService.currentProductGroup$
      .pipe(first(), takeUntil(this._destroy$))
      .subscribe((group) => {
        this.shipmentsState.addShipmentToSelected(group.code, shipmentCode);
      });
  }

  public removeShipmentForCurrentProductGroup(shipmentCode: string): void {
    this.productGroupsService.currentProductGroup$
      .pipe(first(), takeUntil(this._destroy$))
      .subscribe((group) => {
        this.shipmentsState.removeShipmentFromSelected(
          group.code,
          shipmentCode,
        );
      });
  }

  public setAllShipmentsForCurrentProductGroup(): void {
    combineLatest([
      this._unselectedShipmentCodesForCurrentProductGroup$,
      this._getSelectedShipmentCodesByCurrentProductGroup(),
      this.productGroupsService.currentProductGroup$,
    ])
      .pipe(first(), takeUntil(this._destroy$))
      .subscribe(([unselectedShipments, selectedShipments, product]) => {
        const shipments = [...unselectedShipments, ...selectedShipments];
        this.shipmentsState.setSelectedShipments(product.code, shipments);
      });
  }

  public get hasUnselectedShipmentsForCurrentProductGroup$(): Observable<boolean> {
    return this._unselectedShipmentCodesForCurrentProductGroup$.pipe(
      map((codes) => !!codes.length),
    );
  }

  private _getSelectedShipmentCodesByCurrentProductGroup(): Observable<
    string[]
  > {
    return this.productGroupsService.currentProductGroup$.pipe(
      switchMap((group) =>
        this.shipmentsState.getSelectedShipments(group.code),
      ),
    );
  }

  private get _unselectedShipmentCodesForCurrentProductGroup$(): Observable<
    string[]
  > {
    return this.unselectedShipmentsForCurrentProductGroup$.pipe(
      map((shipments) => shipments.map((shipment) => shipment.code)),
    );
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
